/**
 * Recurring Tickets — client-side Yup validation schema (Phase 5).
 *
 * BUG-02 mitigation: the backend does NOT validate frequency / creation_time /
 * start_date / time_zone shapes. The frontend is the only quality gate, so
 * these rules must be strict. See BACKEND_BUGS_QA.md BUG-02.
 *
 * The schema is exposed as a standalone `recurringSectionSchema` so the existing
 * `CreateTicketDrawer` can merge it onto its own base schema at runtime when the
 * "Make Recurring" toggle is ON.
 */

import dayjs from "dayjs";
import * as yup from "yup";

import type { RecurringTicketFrequency } from "shared";

import { frequencyLabelMap } from "../utils/frequencyLabels";

const FREQUENCY_VALUES = Object.keys(frequencyLabelMap) as RecurringTicketFrequency[];
const HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Field names MUST be namespaced under `recurring_*` so they do not collide
 * with the parent drawer's existing flat field names (e.g. `customer`, `user`,
 * `priority`). This keeps the merge surgical and the toggle OFF path untouched.
 */
export interface RecurringFormFieldValues {
  makeRecurring: boolean;
  recurring_frequency?: RecurringTicketFrequency | "";
  recurring_creation_time?: string;
  recurring_start_date?: string;
  recurring_end_date?: string | null;
  recurring_time_zone?: string;
}

export const RECURRING_FIELD_KEYS = [
  "recurring_frequency",
  "recurring_creation_time",
  "recurring_start_date",
  "recurring_end_date",
  "recurring_time_zone",
] as const;

/**
 * Factory: returns a Yup object schema validating ONLY the recurring fields.
 * `companyTimezone` is required so "today" comparisons happen in the MSP tz
 * (matches the canonical date-handling pattern; see RecurringTicketFormSection).
 */
export const buildRecurringSectionSchema = (companyTimezone?: string) =>
  yup.object({
    makeRecurring: yup.boolean().default(false),
    recurring_frequency: yup
      .string()
      .when("makeRecurring", {
        is: true,
        then: (schema) =>
          schema
            .required("Frequency is required")
            .oneOf(FREQUENCY_VALUES, "Pick a valid frequency"),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    recurring_creation_time: yup
      .string()
      .when("makeRecurring", {
        is: true,
        then: (schema) =>
          schema
            .required("Creation time is required")
            .matches(HHMM_REGEX, "Use 24-hour HH:MM format"),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    recurring_start_date: yup
      .string()
      .when("makeRecurring", {
        is: true,
        then: (schema) =>
          schema
            .required("Start date is required")
            .test(
              "start-date-not-in-past",
              "Start date must be today or later",
              (value) => {
                if (!value) return false;
                const start = companyTimezone
                  ? dayjs(value).tz(companyTimezone).startOf("day")
                  : dayjs(value).startOf("day");
                const today = companyTimezone
                  ? dayjs().tz(companyTimezone).startOf("day")
                  : dayjs().startOf("day");
                return start.isValid() && !start.isBefore(today);
              }
            ),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    recurring_end_date: yup
      .string()
      .nullable()
      .when("makeRecurring", {
        is: true,
        then: (schema) =>
          schema.test(
            "end-date-after-start",
            "End date must be after start date",
            function (value) {
              if (!value) return true; // Optional.
              const start = (this.parent as { recurring_start_date?: string })
                .recurring_start_date;
              if (!start) return true;
              const endD = companyTimezone
                ? dayjs(value).tz(companyTimezone)
                : dayjs(value);
              const startD = companyTimezone
                ? dayjs(start).tz(companyTimezone)
                : dayjs(start);
              return endD.isValid() && startD.isValid() && endD.isAfter(startD);
            }
          ),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    // Time zone field removed from UI — default is set in the submit hook payload builder.
    recurring_time_zone: yup.string().nullable().notRequired(),
  });

/**
 * Tz-agnostic schema kept for backwards compatibility with imports/tests that
 * don't have access to the MSP tz. Prefer `buildRecurringSectionSchema(tz)`.
 */
export const recurringSectionSchema = buildRecurringSectionSchema();

export type RecurringSectionSchema = ReturnType<typeof buildRecurringSectionSchema>;

/**
 * Merge helper — returns a new Yup object schema combining `base` with the
 * recurring section rules. Pass `companyTimezone` so "today" comparisons run
 * in the MSP tz.
 *
 * ```ts
 * const schema = mergeRecurringSectionSchema(existingSchema, companyTimezone);
 * ```
 */
export const mergeRecurringSectionSchema = <T extends yup.ObjectSchema<yup.AnyObject>>(
  base: T,
  companyTimezone?: string,
): T => {
  // `.concat()` merges field rules while keeping the existing ones.
  // The cast preserves the original `T` for the caller.
  return base.concat(buildRecurringSectionSchema(companyTimezone) as unknown as T) as T;
};
